import React, { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form, Field, connect } from 'formik'
import { Title, Button, Checkbox } from '@/ui'
import { Add } from '@/app/icons'
import styles from './ConditionsPicker.module.scss'
import { useSelector } from 'react-redux'
import { contractsSelectors } from '@/entity'

const WrappedCheckbox = ({ onChange, value, ...props }) => {
	const handleCheckboxChange = useCallback(() => {
		onChange(!value)
	}, [value, onChange])

	return <Checkbox onChange={handleCheckboxChange} value={value} {...props} />
}

const SelectAll = connect(({ formik: { setValues, values }, label, isTeenager }) => {
	const checkboxValue = useMemo(() => {
		const filteredValues = { ...values }
		if (isTeenager) {
			delete filteredValues.contractWorkingHours
		}
		return !Object.values(filteredValues).includes(false)
	}, [values, isTeenager])

	const handleSelectAll = useCallback(() => {
		let toSet = false
		if (!checkboxValue) {
			toSet = true
		}
		setValues({
			contractTerm: toSet,
			contractPosition: toSet,
			...(isTeenager ? {} : { contractWorkingHours: toSet }), // Не устанавливается, если isTeenager
			contractJobLocation: toSet,
			contractBin: toSet,
			contractJobType: toSet,
			contractOther: toSet,
		})
	}, [checkboxValue, setValues])

	return <Checkbox onChange={handleSelectAll} value={checkboxValue} label={label} />
})

const SubmitButton = connect(({ label, formik: { values = {} }, isTransfer, isTeenager, isChecked }) => {
	const isSelected = useMemo(() => {
		if (isTransfer) {
			return Object.values(values).includes(true) && values.contractBin
		} else {
			return Object.values(values).includes(true)
		}
	}, [values, isTransfer, isTeenager, isChecked])

	const isNoEditAdContract = !isSelected && isTeenager && !isChecked

	return (
		<Button buttonClassName={styles.button} type="submit" disabled={!isSelected || isNoEditAdContract}>
			{label}
		</Button>
	)
})

export const ConditionsPicker = ({
	onSaveChoice,
	onCancelEditing,
	isEdit = false,
	isTransfer,
	setShowCheckbox,
	isChecked,
}) => {
	const { t } = useTranslation()
	const [show, setShow] = useState(isEdit)
	const {
		dcontractDateCode,
		contractEndDate,
		dworkingHoursCode,
		tariffRate,
		establishedPost,
		destablishedPostCode,
		dpartTimeCode,
		dremoteWorkCode,
		dcountryCode,
		ddistrictCode,
		dregionCode,
		dlocalityCode,
		workingPlace,
		otherConditions,
	} = useSelector(contractsSelectors.additionalContract) || {}
	const { age } = useSelector((state) => state.contracts.contract.data)
	const isTeenager = Number.isFinite(age) && age < 18

	const toggleShow = useCallback(() => {
		setShow((value) => !value)
		setShowCheckbox((value) => !value)
	}, [])

	return (
		<Formik
			initialValues={{
				selectAllConditions: !!dcontractDateCode || !!contractEndDate,
				contractTerm: !!dcontractDateCode || !!contractEndDate,
				contractPosition: !!establishedPost || !!destablishedPostCode,
				contractJobType: !!dpartTimeCode,
				contractWorkingHours: !isTeenager && (!!dworkingHoursCode || !!tariffRate),
				contractJobLocation:
					!!dremoteWorkCode || !!dcountryCode || !!ddistrictCode || !!dregionCode || !!dlocalityCode || !!workingPlace,
				contractBin: !!isTransfer,
				contractOther: !!otherConditions,
			}}
			onSubmit={onSaveChoice}
		>
			<Form>
				<Title type="h3" className={styles.title}>
					{t('choose_conditions_to_change')}
				</Title>
				<Button buttonClassName={styles.button} onClick={toggleShow} disabled={isTeenager && !isChecked}>
					<Add className={styles.iconWhite} />
					{t('select')}
				</Button>
				{show && (
					<div className={styles.col}>
						<SelectAll label={t('select_all')} isTeenager={isTeenager} />
						<Field name="contractTerm">
							{({ field }) => <WrappedCheckbox {...field} label={t('change_contract_term')} />}
						</Field>
						<Field name="contractPosition">
							{({ field }) => <WrappedCheckbox {...field} label={t('change_contract_position')} />}
						</Field>
						{!isTeenager ? (
							<Field name="contractWorkingHours">
								{({ field }) => <WrappedCheckbox {...field} label={t('change_working_hours')} />}
							</Field>
						) : null}
						<Field name="contractJobType">
							{({ field }) => <WrappedCheckbox {...field} label={t('change_work_type')} />}
						</Field>
						<Field name="contractJobLocation">
							{({ field }) => <WrappedCheckbox {...field} label={t('change_work_country')} />}
						</Field>
						<Field name="contractOther">
							{({ field }) => <WrappedCheckbox {...field} label={t('change_contract_other')} />}
						</Field>
						{isTransfer && (
							<div className={styles.checkbox__wrapper}>
								<Checkbox label={t('change_iin_bin')} value={true} disabled />
							</div>
						)}
						<div className={styles.footer}>
							<Button buttonClassName={styles.button} type="bare" onClick={onCancelEditing}>
								{t('cancel')}
							</Button>
							<SubmitButton
								isTransfer={isTransfer}
								label={t('continue')}
								isTeenager={isTeenager}
								isChecked={isChecked}
							/>
						</div>
					</div>
				)}
			</Form>
		</Formik>
	)
}
